import { useState, useMemo } from 'react';

// Pricing model parameters (easily adjustable/maintainable)
const PRICING_CONFIG = {
  private: {
    baseRatePerM2: 5.5, // DKK per m2 baseline
    minPrice: 550,
    types: [
      { id: 'standard', name: 'Almindelig standard rengøring', desc: 'Støvsugning, gulvvask, aftørring af overflader, køkken og bad.', multiplier: 1.0 },
      { id: 'grundig', name: 'Grundig hovedrengøring', desc: 'Ekstra dybdegående rengøring inkl. paneler, døre, kalkfjernelse og skabe udvendigt.', multiplier: 1.4 },
      { id: 'fraflytning', name: 'Fraflytnings- / indflytningsrengøring', desc: 'Komplet rengøring af tom bolig til synsklargøring.', multiplier: 1.6 },
    ],
    frequencies: [
      { id: 'weekly', name: 'Ugentligt', discount: 0.15, badge: 'Mest populær (-15%)' },
      { id: 'biweekly', name: 'Hver 14. dag', discount: 0.10, badge: 'Populær (-10%)' },
      { id: 'monthly', name: 'Månedligt', discount: 0.05, badge: '-5%' },
      { id: 'once', name: 'Én enkelt gang', discount: 0.0, badge: 'Enkeltopgave' },
    ],
    extras: [
      { id: 'oven', name: 'Ovnrens indvendigt', price: 250, icon: 'fa-fire-burner' },
      { id: 'fridge', name: 'Køleskabsrengøring indvendigt', price: 200, icon: 'fa-snowflake' },
      { id: 'windows_inside', name: 'Indvendig vinduespudsning', price: 350, icon: 'fa-table-cells' },
      { id: 'limescale', name: 'Ekstra afkalkning af badeværelse', price: 300, icon: 'fa-shower' },
    ],
  },
  commercial: {
    baseRatePerM2: 4.8,
    minPrice: 750,
    types: [
      { id: 'office', name: 'Kontorlokaler & møderum', desc: 'Skriveborde, fællesarealer, affaldshåndtering, køkken og toiletter.', multiplier: 1.0 },
      { id: 'clinic', name: 'Klinik & hygiejniske rum', desc: 'Høje hygiejnekrav, desinficerende rengøring og patientområder.', multiplier: 1.35 },
      { id: 'shop', name: 'Butik & detailhandel', desc: 'Salgsarealer, indgangspartier, personalerum og lagerfaciliteter.', multiplier: 1.15 },
      { id: 'facility', name: 'Erhvervsfællesarealer & trappevask', desc: 'Opgange, kantine og gennemgangsarealer.', multiplier: 1.1 },
    ],
    frequencies: [
      { id: 'daily', name: '1-5 gange ugentligt (Løbende)', discount: 0.20, badge: 'Erhvervsaftale (-20%)' },
      { id: 'weekly', name: 'Ugentlig rengøring', discount: 0.15, badge: '-15%' },
      { id: 'biweekly', name: 'Hver 14. dag', discount: 0.10, badge: '-10%' },
      { id: 'monthly', name: 'Månedligt eftersyn', discount: 0.05, badge: '-5%' },
      { id: 'once', name: 'Enkeltstående erhvervsrengøring', discount: 0.0, badge: 'Ad hoc' },
    ],
    extras: [
      { id: 'windows_comm', name: 'Vinduespolering indvendigt', price: 500, icon: 'fa-table-cells' },
      { id: 'kitchen_deep', name: 'Dybrengøring af tekøkken/kantine', price: 450, icon: 'fa-utensils' },
      { id: 'waste_sort', name: 'Kildesortering & affaldshåndtering', price: 250, icon: 'fa-recycle' },
      { id: 'disinfection', name: 'Fuld overfladedesinfektion', price: 400, icon: 'fa-shield-halved' },
    ],
  },
};

export default function PriceCalculator({ embedded = false }) {
  const [step, setStep] = useState(1);
  const [customerType, setCustomerType] = useState('private'); // 'private' or 'commercial'
  const [sqm, setSqm] = useState(100);
  const [selectedType, setSelectedType] = useState('standard');
  const [frequency, setFrequency] = useState('biweekly');
  const [selectedExtras, setSelectedExtras] = useState([]);

  // Lead form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    website_url: '', // Antispam honeypot
  });
  const [formStatus, setFormStatus] = useState({ state: 'idle', message: '' });

  // Update default type & frequency when toggling customerType
  const handleCustomerTypeChange = (type) => {
    setCustomerType(type);
    setSelectedType(type === 'private' ? 'standard' : 'office');
    setFrequency(type === 'private' ? 'biweekly' : 'weekly');
    setSelectedExtras([]);
  };

  const currentConfig = PRICING_CONFIG[customerType];

  // Calculate estimated price
  const calculation = useMemo(() => {
    const typeObj = currentConfig.types.find((t) => t.id === selectedType) || currentConfig.types[0];
    const freqObj = currentConfig.frequencies.find((f) => f.id === frequency) || currentConfig.frequencies[0];

    const baseCost = Math.max(
      currentConfig.minPrice,
      sqm * currentConfig.baseRatePerM2 * typeObj.multiplier
    );

    const discountedCost = baseCost * (1 - freqObj.discount);

    const extrasCost = selectedExtras.reduce((sum, extraId) => {
      const extra = currentConfig.extras.find((e) => e.id === extraId);
      return sum + (extra ? extra.price : 0);
    }, 0);

    const totalEstimate = Math.round(discountedCost + extrasCost);

    return {
      typeObj,
      freqObj,
      baseCost: Math.round(baseCost),
      discountedCost: Math.round(discountedCost),
      extrasCost,
      totalEstimate,
    };
  }, [customerType, sqm, selectedType, frequency, selectedExtras, currentConfig]);

  const toggleExtra = (id) => {
    setSelectedExtras((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Lead submission
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ state: 'submitting', message: '' });

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim()) {
      setFormStatus({
        state: 'error',
        message: 'Udfyld venligst alle obligatoriske felter (navn, telefon og email).',
      });
      return;
    }

    const payload = {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      address: formData.address.trim(),
      service_type: customerType === 'private' ? 'Privat rengøring' : 'Erhvervsrengøring',
      message: `
--- OPLYSNINGER FRA PRISBEREGNER ---
Kundetype: ${customerType === 'private' ? 'Privat' : 'Erhverv'}
Areal: ${sqm} m²
Rengøringstype: ${calculation.typeObj.name}
Frekvens: ${calculation.freqObj.name}
Tilvalg: ${
        selectedExtras.length > 0
          ? selectedExtras
              .map((id) => currentConfig.extras.find((e) => e.id === id)?.name)
              .join(', ')
          : 'Ingen tilvalg'
      }
Vejledende estimat: Ca. ${calculation.totalEstimate} kr. (${customerType === 'private' ? 'inkl. moms' : 'ekskl. moms'}) pr. gang
Brugerens bemærkning: ${formData.notes || 'Ingen yderligere bemærkninger'}
      `.trim(),
      website_url: formData.website_url, // Honeypot
    };

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setFormStatus({
          state: 'success',
          message: 'Mange tak! Vi har modtaget dine beregningsoplysninger og vender hurtigt tilbage med et uforpligtende, skræddersyet tilbud.',
        });
      } else {
        const errorData = await res.json().catch(() => null);
        setFormStatus({
          state: 'error',
          message: errorData?.message || 'Der opstod en fejl. Ring venligst direkte til os på 31 68 67 64.',
        });
      }
    } catch (err) {
      console.error(err);
      setFormStatus({
        state: 'error',
        message: 'Forbindelsesfejl. Tjek din internetforbindelse eller ring på 31 68 67 64.',
      });
    }
  };

  return (
    <div className={`w-full ${embedded ? '' : 'max-w-7xl mx-auto my-8'}`}>
      
      {/* Container Box */}
      <div className="bg-white border border-slate-200/90 shadow-xl overflow-hidden">
        
        {/* Header with Step indicator */}
        <div className="bg-black text-white p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white">
                Beregn din rengøringspris på få sekunder
              </h2>
            </div>
            
            {/* Progress / Step status */}
            <div className="flex items-center gap-1.5 self-start sm:self-center text-xs sm:text-sm font-semibold text-white/90">
              <span>Trin {step} af 5</span>
            </div>
          </div>

          {/* Stepper Progress Bar */}
          <div className="mt-6 grid grid-cols-5 gap-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                onClick={() => setStep(s)}
                className={`h-2 transition-all duration-300 ${
                  s === step
                    ? 'bg-white ring-2 ring-white/50'
                    : s < step
                    ? 'bg-white/80 cursor-pointer'
                    : 'bg-white/20'
                }`}
                aria-label={`Gå til trin ${s}`}
              />
            ))}
          </div>
        </div>

        {/* Wizard Content Body */}
        <div className="p-6 sm:p-8 lg:p-10">
          
          {/* TRIN 1: Kundetype */}
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div className="text-center sm:text-left">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                  Trin 1: Hvem skal have udført rengøringen?
                </h3>
                <p className="text-slate-600 text-sm mt-1">
                  Vælg om opgaven gælder en privat bolig eller en virksomhed/erhverv.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <button
                  type="button"
                  onClick={() => handleCustomerTypeChange('private')}
                  className={`p-6 border-2 text-left transition-all duration-200 cursor-pointer flex flex-col justify-between ${
                    customerType === 'private'
                      ? 'border-black bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-2xl text-slate-900">
                      <i className="fa-solid fa-house" aria-hidden="true" />
                    </div>
                    {customerType === 'private' && (
                      <span className="w-6 h-6 bg-black text-white flex items-center justify-center text-xs">
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900">Privat rengøring</h4>
                    <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">
                      Til boliger, huse, lejligheder og sommerhuse. Fleksible intervaller og fast kontaktperson.
                    </p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleCustomerTypeChange('commercial')}
                  className={`p-6 border-2 text-left transition-all duration-200 cursor-pointer flex flex-col justify-between ${
                    customerType === 'commercial'
                      ? 'border-black bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-2xl text-slate-900">
                      <i className="fa-solid fa-building" aria-hidden="true" />
                    </div>
                    {customerType === 'commercial' && (
                      <span className="w-6 h-6 bg-black text-white flex items-center justify-center text-xs">
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900">Erhvervsrengøring</h4>
                    <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">
                      Til kontorer, klinikker, butikker og virksomhedslokaler. Høj stabilitet og skræddersyede aftaler.
                    </p>
                  </div>
                </button>
              </div>

              <div className="pt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-6 py-3 rounded-full font-bold text-sm bg-black text-white hover:bg-slate-800 transition-all flex items-center gap-2 cursor-pointer"
                >
                  <span>Næste: Angiv areal</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          {/* TRIN 2: Størrelse (m²) */}
          {step === 2 && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                  Trin 2: Hvor mange kvadratmeter (m²) skal rengøres?
                </h3>
                <p className="text-slate-600 text-sm mt-1">
                  Træk i slideren eller tast det omtrentlige areal.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 p-6 text-center space-y-6">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-4xl sm:text-5xl font-black text-black tracking-tight">
                    {sqm}
                  </span>
                  <span className="text-lg sm:text-xl font-bold text-slate-500">m²</span>
                </div>

                {/* Range Slider */}
                <input
                  type="range"
                  min="30"
                  max="500"
                  step="5"
                  value={sqm}
                  onChange={(e) => setSqm(Number(e.target.value))}
                  className="w-full h-3 bg-slate-200 appearance-none cursor-pointer accent-emerald-600"
                  aria-label="Antal kvadratmeter"
                />

                <div className="flex justify-between text-xs font-semibold text-slate-500 px-1">
                  <span>30 m²</span>
                  <span>150 m²</span>
                  <span>300 m²</span>
                  <span>500+ m²</span>
                </div>

                {/* Quick Presets */}
                <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                  {[60, 90, 120, 160, 220, 300].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setSqm(preset)}
                      className={`px-3.5 py-1.5 text-xs font-bold transition-colors cursor-pointer ${
                        sqm === preset
                          ? 'bg-black text-white'
                          : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {preset} m²
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-5 py-2.5 rounded-full font-semibold text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                  <span>Tilbage</span>
                </button>

                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="px-6 py-3 rounded-full font-bold text-sm bg-black text-white hover:bg-slate-800 transition-all flex items-center gap-2 cursor-pointer"
                >
                  <span>Næste: Vælg rengøringstype</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          {/* TRIN 3: Rengøringstype */}
          {step === 3 && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                  Trin 3: Hvilken form for rengøring har du brug for?
                </h3>
                <p className="text-slate-600 text-sm mt-1">
                  Vælg den type, der bedst matcher dit behov.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {currentConfig.types.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedType(t.id)}
                    className={`p-4 sm:p-5 border-2 text-left transition-all duration-200 cursor-pointer flex items-start justify-between gap-4 ${
                      selectedType === t.id
                        ? 'border-black bg-slate-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div>
                      <h4 className="text-base font-bold text-slate-900">{t.name}</h4>
                      <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">{t.desc}</p>
                    </div>
                    <div className="shrink-0 mt-0.5">
                      <span
                        className={`w-6 h-6 flex items-center justify-center text-xs ${
                          selectedType === t.id
                            ? 'bg-black text-white'
                            : 'border-2 border-slate-300 text-transparent'
                        }`}
                      >
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="pt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-5 py-2.5 rounded-full font-semibold text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                  <span>Tilbage</span>
                </button>

                <button
                  type="button"
                  onClick={() => setStep(4)}
                  className="px-6 py-3 rounded-full font-bold text-sm bg-black text-white hover:bg-slate-800 transition-all flex items-center gap-2 cursor-pointer"
                >
                  <span>Næste: Vælg frekvens</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          {/* TRIN 4: Frekvens */}
          {step === 4 && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                  Trin 4: Hvor ofte ønsker du rengøring?
                </h3>
                <p className="text-slate-600 text-sm mt-1">
                  Faste intervaller giver den bedste rabat og et vedvarende rent resultat.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {currentConfig.frequencies.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFrequency(f.id)}
                    className={`p-4 sm:p-5 border-2 text-left transition-all duration-200 cursor-pointer flex flex-col justify-between ${
                      frequency === f.id
                        ? 'border-black bg-slate-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-slate-800">
                        {f.badge}
                      </span>
                      <span
                        className={`w-5 h-5 flex items-center justify-center text-xs ${
                          frequency === f.id
                            ? 'bg-black text-white'
                            : 'border-2 border-slate-300 text-transparent'
                        }`}
                      >
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      </span>
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-slate-900">{f.name}</h4>
                    </div>
                  </button>
                ))}
              </div>

              <div className="pt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="px-5 py-2.5 rounded-full font-semibold text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                  <span>Tilbage</span>
                </button>

                <button
                  type="button"
                  onClick={() => setStep(5)}
                  className="px-6 py-3 rounded-full font-bold text-sm bg-black text-white hover:bg-slate-800 transition-all flex items-center gap-2 cursor-pointer"
                >
                  <span>Næste: Ekstra tilvalg</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          {/* TRIN 5: Tilvalg & Prisresultat & Lead Form */}
          {step === 5 && (
            <div className="space-y-8 animate-in fade-in duration-200">
              
              {/* Optional extras toggles */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">
                  Trin 5: Ekstra tilvalg (valgfrit)
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Sæt kryds ved eventuelle ekstra ydelser, du ønsker med i overvejelserne.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentConfig.extras.map((extra) => {
                    const isChecked = selectedExtras.includes(extra.id);
                    return (
                      <button
                        key={extra.id}
                        type="button"
                        onClick={() => toggleExtra(extra.id)}
                        className={`p-3.5 border-2 text-left transition-all cursor-pointer flex items-center justify-between gap-3 ${
                          isChecked
                            ? 'border-black bg-slate-50 text-slate-950 font-semibold'
                            : 'border-slate-200 hover:border-slate-300 text-slate-700 bg-white'
                        }`}
                      >
                        <span className="flex items-center gap-2.5 text-sm">
                          <i className={`fa-solid ${extra.icon} text-slate-900 text-xs`} aria-hidden="true" />
                          <span>{extra.name}</span>
                        </span>
                        <span
                          className={`w-5 h-5 flex items-center justify-center text-xs ${
                            isChecked
                              ? 'bg-black text-white'
                              : 'border border-slate-300 bg-white'
                          }`}
                        >
                          {isChecked && <i className="fa-solid fa-check" aria-hidden="true" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ESTIMATED PRICE BOX */}
              <div className="bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950 text-white p-6 sm:p-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 block mb-1">
                      Vejledende Prisestimat
                    </span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl sm:text-4xl font-extrabold text-white">
                        Ca. {calculation.totalEstimate} kr.
                      </span>
                      <span className="text-xs sm:text-sm text-slate-300">
                        {customerType === 'private' ? '/ gang (inkl. moms)' : '/ gang (ekskl. moms)'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-2 max-w-lg leading-relaxed">
                      * Beløbet er en vejledende prisindikation. Den endelige pris afhænger af boligens/lokalets konkrete indretning, opgavens omfang og særlige ønsker.
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-md p-4 text-xs space-y-1.5 text-slate-300 min-w-[220px]">
                    <div className="flex justify-between">
                      <span>Kundetype:</span>
                      <strong className="text-white">{customerType === 'private' ? 'Privat' : 'Erhverv'}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Areal:</span>
                      <strong className="text-white">{sqm} m²</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Interval:</span>
                      <strong className="text-white">{calculation.freqObj.name}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Rengøringstype:</span>
                      <strong className="text-white">{calculation.typeObj.name.split(' ')[0]}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* LEAD GENERATION FORM */}
              <div className="bg-slate-50 border border-slate-200 p-6 sm:p-8">
                <div className="mb-6">
                  <h4 className="text-lg sm:text-xl font-bold text-slate-900">
                    Få dit endelige, uforpligtende tilbud
                  </h4>
                  <p className="text-slate-600 text-sm mt-1">
                    Dine beregningsdata er automatisk vedhæftet. Indtast dine kontaktoplysninger herunder:
                  </p>
                </div>

                {formStatus.state === 'success' ? (
                  <div className="bg-emerald-100 border border-emerald-300 p-6 text-center text-emerald-900">
                    <i className="fa-solid fa-circle-check text-3xl text-emerald-600 mb-2" aria-hidden="true" />
                    <h5 className="font-bold text-lg mb-1">Tak for din forespørgsel!</h5>
                    <p className="text-sm">{formStatus.message}</p>
                  </div>
                ) : (
                  <form onSubmit={handleLeadSubmit} className="space-y-4">
                    {/* Antispam honeypot */}
                    <div className="sr-only absolute -left-[9999px] -top-[9999px]" aria-hidden="true">
                      <label htmlFor="calc_website_url">Website URL (leave blank)</label>
                      <input
                        type="text"
                        id="calc_website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {formStatus.state === 'error' && (
                      <div className="p-3.5 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
                        <i className="fa-solid fa-circle-exclamation text-red-500" aria-hidden="true" />
                        <span>{formStatus.message}</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label htmlFor="calc_name" className="block text-xs font-bold text-slate-700 mb-1 text-left">
                          Dit navn <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="calc_name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Navn"
                          className="w-full px-0 py-2 bg-transparent border-0 border-b-2 border-slate-200 text-slate-900 text-sm focus:border-emerald-600 focus:outline-none text-left rounded-none transition-colors"
                        />
                      </div>

                      <div>
                        <label htmlFor="calc_phone" className="block text-xs font-bold text-slate-700 mb-1 text-left">
                          Telefonnummer <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="calc_phone"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="31 68 67 64"
                          className="w-full px-0 py-2 bg-transparent border-0 border-b-2 border-slate-200 text-slate-900 text-sm focus:border-emerald-600 focus:outline-none text-left rounded-none transition-colors"
                        />
                      </div>

                      <div>
                        <label htmlFor="calc_email" className="block text-xs font-bold text-slate-700 mb-1 text-left">
                          Emailadresse <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="calc_email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="din@email.dk"
                          className="w-full px-0 py-2 bg-transparent border-0 border-b-2 border-slate-200 text-slate-900 text-sm focus:border-emerald-600 focus:outline-none text-left rounded-none transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="calc_notes" className="block text-xs font-bold text-slate-700 mb-1 text-left">
                        Evt. kommentar eller adresse
                      </label>
                      <input
                        type="text"
                        id="calc_notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="F.eks. Adresse i Nørre Aaby eller særlige tidspunkter..."
                        className="w-full px-0 py-2 bg-transparent border-0 border-b-2 border-slate-200 text-slate-900 text-sm focus:border-emerald-600 focus:outline-none text-left rounded-none transition-colors"
                      />
                    </div>

                    <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <button
                        type="button"
                        onClick={() => setStep(4)}
                        className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1.5 cursor-pointer"
                      >
                        <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                        <span>Ret i beregningen</span>
                      </button>

                      <button
                        type="submit"
                        disabled={formStatus.state === 'submitting'}
                        className="w-full sm:w-auto px-8 py-3.5 rounded-full font-bold text-sm text-white bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                      >
                        {formStatus.state === 'submitting' ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin text-sm" aria-hidden="true" />
                            <span>Sender tilbudsanmodning...</span>
                          </>
                        ) : (
                          <>
                            <span>Få mit endelige tilbud</span>
                            <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}
